// useFloatingHudPosition — drag + position-persistence hook for HUD-style
// floating panels (canvas zoom controls, global search bar, future
// per-user pinboards).
//
// Differs from useDraggablePanel in two ways:
//   1. Position is persisted in localStorage keyed by `id`, so the
//      HUD reappears where the user left it across sessions.
//   2. There's no `open` reset semantics — the HUD is always mounted;
//      the panel state is "just position + fold".
//
// Bounded into the viewport on every render so a window resize can't
// strand the HUD off-screen.

import { useCallback, useEffect, useRef, useState } from 'react';

export interface FloatingHudPosition {
  x: number;
  y: number;
}

export interface UseFloatingHudPositionArgs {
  /** Stable id used for the localStorage key. */
  id: string;
  /** Position used the first time the HUD is mounted in this browser. */
  defaultPosition: FloatingHudPosition;
  /** Approximate HUD dimensions used for viewport-clamp + initial-x mirror.
   *  Inexact is fine — we just don't want the HUD half-off-screen. */
  approxSize?: { width: number; height: number };
}

export interface FloatingHudPositionApi {
  position: FloatingHudPosition;
  folded: boolean;
  setFolded: (folded: boolean) => void;
  setPosition: (p: FloatingHudPosition) => void;
  dragHandlers: {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
  };
  /** Restore the configured default position (also clears localStorage). */
  reset: () => void;
}

const PREFIX = 'fw.hudpos.';
const FOLD_PREFIX = 'fw.hudfold.';

function safeRead(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeWrite(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* private mode */ }
}
function safeRemove(key: string) {
  try { localStorage.removeItem(key); } catch { /* private mode */ }
}

function clampToViewport(p: FloatingHudPosition, size: { width: number; height: number }): FloatingHudPosition {
  if (typeof window === 'undefined') return p;
  const margin = 8;
  const maxX = Math.max(margin, window.innerWidth - size.width - margin);
  const maxY = Math.max(margin, window.innerHeight - size.height - margin);
  return {
    x: Math.min(Math.max(margin, p.x), maxX),
    y: Math.min(Math.max(margin, p.y), maxY),
  };
}

function readInitial(
  id: string,
  fallback: FloatingHudPosition,
  size: { width: number; height: number },
): FloatingHudPosition {
  const raw = safeRead(PREFIX + id);
  if (!raw) return clampToViewport(fallback, size);
  try {
    const parsed = JSON.parse(raw) as { x?: unknown; y?: unknown };
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
      return clampToViewport({ x: parsed.x, y: parsed.y }, size);
    }
  } catch { /* fall through */ }
  return clampToViewport(fallback, size);
}

function readInitialFold(id: string): boolean {
  return safeRead(FOLD_PREFIX + id) === '1';
}

export function useFloatingHudPosition({
  id,
  defaultPosition,
  approxSize = { width: 320, height: 64 },
}: UseFloatingHudPositionArgs): FloatingHudPositionApi {
  const sizeRef = useRef(approxSize);
  sizeRef.current = approxSize;

  const [position, setPositionState] = useState<FloatingHudPosition>(() =>
    readInitial(id, defaultPosition, approxSize),
  );
  const [folded, setFoldedState] = useState<boolean>(() => readInitialFold(id));
  const dragRef = useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(null);

  // Persist on every position / fold change.
  useEffect(() => {
    safeWrite(PREFIX + id, JSON.stringify(position));
  }, [id, position]);
  useEffect(() => {
    safeWrite(FOLD_PREFIX + id, folded ? '1' : '0');
  }, [id, folded]);

  // Re-clamp on window resize so the HUD never strands off-screen.
  useEffect(() => {
    const onResize = () => {
      setPositionState((prev) => clampToViewport(prev, sizeRef.current));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const setPosition = useCallback((p: FloatingHudPosition) => {
    setPositionState(clampToViewport(p, sizeRef.current));
  }, []);

  const setFolded = useCallback((next: boolean) => {
    setFoldedState(next);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, posX: position.x, posY: position.y };
    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setPositionState(clampToViewport({
        x: dragRef.current.posX + ev.clientX - dragRef.current.startX,
        y: dragRef.current.posY + ev.clientY - dragRef.current.startY,
      }, sizeRef.current));
    };
    const handleMouseUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [position]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    dragRef.current = { startX: touch.clientX, startY: touch.clientY, posX: position.x, posY: position.y };
    const handleTouchMove = (ev: TouchEvent) => {
      if (!dragRef.current || ev.touches.length !== 1) return;
      ev.preventDefault();
      setPositionState(clampToViewport({
        x: dragRef.current.posX + ev.touches[0].clientX - dragRef.current.startX,
        y: dragRef.current.posY + ev.touches[0].clientY - dragRef.current.startY,
      }, sizeRef.current));
    };
    const handleTouchEnd = () => {
      dragRef.current = null;
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
  }, [position]);

  const reset = useCallback(() => {
    safeRemove(PREFIX + id);
    safeRemove(FOLD_PREFIX + id);
    setPositionState(clampToViewport(defaultPosition, sizeRef.current));
    setFoldedState(false);
  }, [id, defaultPosition]);

  return {
    position,
    folded,
    setFolded,
    setPosition,
    dragHandlers: { onMouseDown: handleMouseDown, onTouchStart: handleTouchStart },
    reset,
  };
}
